/**
 * Alert Configurator - Modal do konfiguracji customowych reguł alertów
 */

import { useState } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import type { AlertRule, AlertCondition, AlertColor } from '../../types/orderAlerts';
import { ALERT_COLOR_CONFIG } from '../../types/orderAlerts';
import { AVAILABLE_FIELDS, AVAILABLE_OPERATORS, getOperatorLabel, getFieldLabel } from '../../utils/alertEngine';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

interface AlertConfiguratorProps {
  isOpen: boolean;
  onClose: () => void;
  rules: AlertRule[];
  onSave: (rules: AlertRule[]) => void;
}

export const AlertConfigurator = ({ isOpen, onClose, rules, onSave }: AlertConfiguratorProps) => {
  const [editMode, setEditMode] = useState<'list' | 'edit'>('list');
  const [currentRule, setCurrentRule] = useState<AlertRule | null>(null);

  const handleAddNew = () => {
    const newRule: AlertRule = {
      id: `custom-${Date.now()}`,
      name: 'Nowa reguła',
      description: '',
      color: 'orange',
      priority: 10,
      enabled: true,
      conditions: [
        {
          field: 'order_date',
          operator: 'isNotEmpty'
        }
      ],
      conditionLogic: 'AND'
    };
    setCurrentRule(newRule);
    setEditMode('edit');
  };

  const handleEdit = (rule: AlertRule) => {
    setCurrentRule({ ...rule });
    setEditMode('edit');
  };

  const handleDelete = (ruleId: string) => {
    if (confirm('Czy na pewno chcesz usunąć tę regułę?')) {
      const updated = rules.filter(r => r.id !== ruleId);
      onSave(updated);
    }
  };

  const handleToggle = (ruleId: string) => {
    const updated = rules.map(r =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    );
    onSave(updated);
  };

  const handleSaveRule = () => {
    if (!currentRule) return;

    // Validate
    if (!currentRule.name.trim()) {
      alert('Podaj nazwę reguły');
      return;
    }
    if (currentRule.conditions.length === 0) {
      alert('Dodaj przynajmniej jeden warunek');
      return;
    }

    const existingIndex = rules.findIndex(r => r.id === currentRule.id);
    let updated: AlertRule[];
    if (existingIndex >= 0) {
      // Update existing
      updated = rules.map(r => r.id === currentRule.id ? currentRule : r);
    } else {
      // Add new
      updated = [...rules, currentRule];
    }

    onSave(updated);
    setEditMode('list');
    setCurrentRule(null);
  };

  const handleCancel = () => {
    setEditMode('list');
    setCurrentRule(null);
  };

  const addCondition = () => {
    if (!currentRule) return;
    setCurrentRule({
      ...currentRule,
      conditions: [
        ...currentRule.conditions,
        { field: 'order_date', operator: 'isNotEmpty' }
      ]
    });
  };

  const updateCondition = (index: number, updated: Partial<AlertCondition>) => {
    if (!currentRule) return;
    const newConditions = [...currentRule.conditions];
    newConditions[index] = { ...newConditions[index], ...updated };
    setCurrentRule({ ...currentRule, conditions: newConditions });
  };

  const removeCondition = (index: number) => {
    if (!currentRule) return;
    setCurrentRule({
      ...currentRule,
      conditions: currentRule.conditions.filter((_, i) => i !== index)
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editMode === 'list' ? 'Konfiguracja alertów' : 'Edycja reguły'}
      size="xl"
    >
      <div className="space-y-4">

        {editMode === 'list' ? (
          <>
            <div className="mb-4 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Zarządzaj regułami alertów. Wiersze w tabeli będą podświetlane według najważniejszej reguły (najwyższy priorytet).
              </p>
              <Button onClick={handleAddNew}>
                <PlusIcon className="h-4 w-4 mr-1" /> Dodaj regułę
              </Button>
            </div>

            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {rules.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Brak reguł. Kliknij "Dodaj regułę" aby utworzyć pierwszą.
                </div>
              )}

              {rules.map((rule) => {
                const colorConfig = ALERT_COLOR_CONFIG[rule.color];
                // Fallback dla usuniętych kolorów (yellow, blue)
                const bgClass = rule.enabled && colorConfig ? colorConfig.bg : 'bg-gray-100';
                return (
                  <div
                    key={rule.id}
                    className={`border rounded-lg p-4 ${bgClass}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={() => handleToggle(rule.id)}
                            className="h-4 w-4"
                          />
                          <span className="font-semibold">{rule.name}</span>
                          <span className="text-xs text-gray-500">
                            (Priorytet: {rule.priority})
                          </span>
                        </div>
                        {rule.description && (
                          <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                        )}
                        <div className="text-xs text-gray-600">
                          <strong>Warunki ({rule.conditionLogic || 'AND'}):</strong>
                          <ul className="list-disc list-inside mt-1">
                            {rule.conditions.map((cond, idx) => (
                              <li key={idx}>
                                {cond.description || `${getFieldLabel(cond.field)} ${getOperatorLabel(cond.operator)} ${cond.value ?? ''}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(rule)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="Edytuj"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule.id)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Usuń"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Zamknij
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Edit mode */}
            {currentRule && (
              <div className="space-y-4">
                {/* Basic info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nazwa reguły</label>
                    <input
                      type="text"
                      value={currentRule.name}
                      onChange={(e) => setCurrentRule({ ...currentRule, name: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="Np. Proponowany załadunek przeszedł"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priorytet</label>
                    <input
                      type="number"
                      value={currentRule.priority}
                      onChange={(e) => setCurrentRule({ ...currentRule, priority: parseInt(e.target.value) || 0 })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="100 = krytyczny, 50 = uwaga, 10 = info"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Opis (opcjonalny)</label>
                  <textarea
                    value={currentRule.description || ''}
                    onChange={(e) => setCurrentRule({ ...currentRule, description: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    rows={2}
                    placeholder="Dłuższy opis reguły..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Kolor alertu</label>
                    <select
                      value={currentRule.color}
                      onChange={(e) => setCurrentRule({ ...currentRule, color: e.target.value as AlertColor })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      {Object.entries(ALERT_COLOR_CONFIG).map(([color, config]) => (
                        <option key={color} value={color}>
                          {config.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Logika warunków</label>
                    <select
                      value={currentRule.conditionLogic || 'AND'}
                      onChange={(e) => setCurrentRule({ ...currentRule, conditionLogic: e.target.value as 'AND' | 'OR' })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="AND">AND (wszystkie muszą być spełnione)</option>
                      <option value="OR">OR (przynajmniej jeden musi być spełniony)</option>
                    </select>
                  </div>
                </div>

                {/* Conditions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Warunki</label>
                    <button
                      onClick={addCondition}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" /> Dodaj warunek
                    </button>
                  </div>

                  <div className="space-y-3">
                    {currentRule.conditions.map((cond, idx) => {
                      const selectedOperator = AVAILABLE_OPERATORS.find(op => op.value === cond.operator);
                      const requiresValue = selectedOperator?.requiresValue ?? false;

                      return (
                        <div key={idx} className="border border-gray-300 rounded p-3 bg-white">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 grid grid-cols-3 gap-2">
                              {/* Field */}
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Pole</label>
                                <select
                                  value={cond.field}
                                  onChange={(e) => updateCondition(idx, { field: e.target.value })}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                >
                                  {AVAILABLE_FIELDS.map(f => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Operator */}
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Operator</label>
                                <select
                                  value={cond.operator}
                                  onChange={(e) => updateCondition(idx, { operator: e.target.value as any })}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                >
                                  {AVAILABLE_OPERATORS.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Value */}
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Wartość</label>
                                {requiresValue ? (
                                  <input
                                    type="text"
                                    value={cond.value ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateCondition(idx, { value: isNaN(Number(val)) ? val : Number(val) });
                                    }}
                                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                    placeholder="Np. 3"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-400 py-1">— (nie wymagana)</div>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => removeCondition(idx)}
                              className="mt-6 p-1 text-red-600 hover:text-red-800"
                              title="Usuń warunek"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>

                          {/* Description */}
                          <div className="mt-2">
                            <label className="block text-xs text-gray-600 mb-1">Opis warunku (opcjonalny)</label>
                            <input
                              type="text"
                              value={cond.description || ''}
                              onChange={(e) => updateCondition(idx, { description: e.target.value })}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                              placeholder="Np. Data proponowanego załadunku < dzisiaj"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancel}>
                    Anuluj
                  </Button>
                  <Button onClick={handleSaveRule}>
                    Zapisz regułę
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
